import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  SearchBox,
  Configure,
  InstantSearch,
  RefinementList,
  connectStateResults,
  QueryRuleCustomData
} from 'react-instantsearch-dom';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import Hits from './results-list';
import MobileTrigger from './left-rail-mobile-trigger';
import NoResults from './results-none';
import PageHeader from '../../tissues/page-header';
import Refinements from './left-rail';
import ResultsHeader from './results-header';

import { Content } from '../../theme/page';
import PLPMeta from '../../utils/meta';

const getCategories = (props) => {
  /**
   * Convert string into category array on pattern of SLI
   */
  if (!props?.searchState?.hierarchicalMenu) {
    return null;
  }

  const categories = props.searchState.hierarchicalMenu['categories.lvl0'];
  return categories.split(' > ').reduce((acc, cur, index) => {
    if (index === 0) {
      acc.push(cur.toLowerCase());
      return acc;
    }
    acc.push(`${acc[index - 1]}_${cur.toLowerCase().replace(/\s/g, '')}`);
    return acc;
  }, []);
};

const getVariants = (props) => {
  /**
   * Merge Age and Price into formatted array
   */
  let variants = [];
  if (Object.keys(props.searchState).includes('refinementList')) {
    const ageRange = props.searchState.refinementList['variants.age_range'];
    variants = ageRange || [];
  }

  let priceRange = null;

  if (Object.keys(props.searchState).includes('range')) {
    const { min, max } = props.searchState.range['variants.maisonette_sale'];
    if (min && max) {
      priceRange = `price:${min}-${max}`;
    }
    if (min && !max) {
      priceRange = `price:${min}`;
    }
    if (!min && max) {
      priceRange = `price:${max}`;
    }
    variants = [...variants, priceRange];
  }

  return variants.length ? variants : null;
};

const PLP = connectStateResults(({
  edit,
  lpc,
  brand,
  trend,
  searchResults,
  searchState
}) => {
  const hasResults = searchResults && searchResults.nbHits !== 0;

  return (
    <main id="maincontent">
      <MobileTrigger isBrandRefined={!!brand} css={{ display: !hasResults ? 'none' : undefined }} />

      <ResultsHeader lpc={lpc} edit={edit} brand={brand} trend={trend} />

      <Content css={{
        display: hasResults ? 'flex' : 'none',
        alignItems: 'flex-start'
      }}
      >
        <Refinements isBrandRefined={!!brand} />
        <Hits />
      </Content>

      <Content css={{ display: hasResults ? 'none' : undefined }}>
        <NoResults searchState={searchState} searchResults={searchResults} />
      </Content>
    </main>
  );
});

PLP.defaultProps = {
  brand: null,
  edit: '',
  lpc: '',
  trend: ''
};

PLP.propTypes = {
  brand: PropTypes.object,
  edit: PropTypes.string,
  lpc: PropTypes.string,
  trend: PropTypes.string
};

const Algolia = (props) => {
  const router = useRouter();

  const [trend] = useState(props?.trend ?? props.searchState?.refinementList?.trends?.[0]);
  const [brand] = useState(props?.brand ?? props.searchState.refinementList?.brand?.[0]);
  const [edit] = useState(props?.edit ?? props.searchState?.refinementList?.edits?.[0]);
  const [lpc] = useState(props?.lpc ?? props.searchState?.refinementList?.lpc?.[0]);
  const [hasFilter, setHasFilter] = useState(false);
  const [attributeFilter, setAttributeFilter] = useState('');
  const { active_mini, minis } = useSelector((state) => state.petites);
  const [miniFilters, setMiniFilters] = useState({});
  const categories = getCategories(props);
  const variants = getVariants(props);
  const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);

  const formatFilter = (pathname) => {
    const pathArray = pathname.slice(1).split('/');

    if (pathArray[0] === 'shop') return null;
    let filter = `${pathArray[0]}:`;

    let cleanValue = pathArray[1];
    // if there is a query param, remove it
    // eslint-disable-next-line prefer-destructuring
    if (pathArray[1]?.includes('?')) cleanValue = pathArray[1].split('?')[0];

    // confirm that the page is /trends/on-sale
    if (cleanValue === 'on-sale') {
      if (cleanValue.includes('-')) {
        const valueArray = cleanValue.split('-');
        const formattedValue = `"${valueArray.reduce((a, b) => `${capitalizeFirstLetter(a)} ${capitalizeFirstLetter(b)}`)}"`;

        filter += formattedValue;
      } else {
        filter += capitalizeFirstLetter(cleanValue);
      }

      return filter;
    }

    return null;
  };

  useEffect(() => {
    if (router?.asPath) {
      const formattedFilter = formatFilter(router.asPath);
      if (formattedFilter) {
        setHasFilter(true);
        setAttributeFilter(formattedFilter);
      }
    }
  }, []);

  useEffect(() => {
    if (active_mini && minis.length) {
      const mini = minis.find((x) => x.id === active_mini);

      if (mini) {
        const refinements = {};

        const { gender_taxons, age_range_taxons } = mini;

        if (gender_taxons) refinements.gender = gender_taxons.map((x) => x.name);
        if (age_range_taxons) refinements['variants.age_range'] = age_range_taxons.map((x) => x.name);

        setMiniFilters(refinements);
      }
    } else setMiniFilters({});
  }, [active_mini, minis]);

  return (
    <>
      <PLPMeta
        {...props}
        trend={trend}
        brand={brand}
        edit={edit}
        lpc={lpc}
        algoliaCategories={categories}
        variants={variants}
      />
      <InstantSearch
        searchClient={props.searchClient}
        resultsState={props.resultsState}
        onSearchStateChange={props.onSearchStateChange}
        searchState={props.searchState}
        createURL={props.createURL}
        indexName={props.indexName}
        {...props}
      >
        { hasFilter ? (
          <Configure
            hitsPerPage={24}
            ruleContexts={[props.lpc]}
            filters={attributeFilter}
            clickAnalytics
          />
        ) : (
          <Configure hitsPerPage={24} ruleContexts={[props.lpc]} clickAnalytics />
        )}

        <span css={{ display: 'none' }}>
          <RefinementList attribute="trends" />
          { props.edit && <RefinementList attribute="edits" defaultRefinement={[props.edit]} /> }
          { props.brand && <RefinementList attribute="brand" defaultRefinement={[props.brand.name ?? null]} /> }
          { miniFilters.gender && <RefinementList attribute="gender" defaultRefinement={miniFilters.gender} /> }
          { miniFilters['variants.age_range'] && <RefinementList attribute="variants.age_range" defaultRefinement={props.miniFilters['variants.age_range']} /> }
        </span>

        <SearchBox css={{ display: 'none' }} />

        <QueryRuleCustomData>
          {({ items }) => {
            if (props.edit) {
              const [item] = items;

              return item && item.name
                ? <PageHeader key={item.name} heading={item.name} />
                : null;
            }

            if (props.lpc) {
              const [item] = items;

              return item && item.title
                ? <PageHeader key={item.title} heading={item.title} />
                : null;
            }
            return null;
          }}
        </QueryRuleCustomData>

        <PLP
          brand={props.brand}
          edit={props.edit}
          lpc={props.lpc}
          trend={props.trend}
        />
      </InstantSearch>
    </>
  );
};

Algolia.defaultProps = {
  brand: null,
  edit: null,
  children: null,
  miniFilters: {},
  lpc: null,
  trend: null
};

Algolia.propTypes = {
  brand: PropTypes.object,
  children: PropTypes.node,
  createURL: PropTypes.func.isRequired,
  edit: PropTypes.string,
  miniFilters: PropTypes.object,
  indexName: PropTypes.string.isRequired,
  lpc: PropTypes.string,
  onSearchStateChange: PropTypes.func.isRequired,
  resultsState: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  searchClient: PropTypes.object.isRequired,
  searchState: PropTypes.object.isRequired,
  trend: PropTypes.string
};

export default Algolia;
